import React, { useRef, useState } from "react";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";

// Carrusel de fotos para la card de habitación, estilo Mercado Libre:
// swipe nativo en mobile (scroll-snap) + flechas en desktop + puntitos.
// Vive dentro de un <Link>, por eso las flechas/puntos frenan la navegación.
const MAX_IMAGES = 10;

const Slide = ({ src, alt, onLoad, zoomOnHover }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative w-full h-full flex-shrink-0 snap-center overflow-hidden">
      {!loaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <img
        className={`w-full h-full object-cover transition-transform duration-700 ${
          zoomOnHover ? "group-hover:scale-105" : ""
        } ${loaded ? "opacity-100" : "opacity-0"}`}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => {
          setLoaded(true);
          if (onLoad) onLoad();
        }}
      />
    </div>
  );
};

const RoomImageCarousel = ({ images = [], alt = "", onFirstLoad }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const imgs = images.slice(0, MAX_IMAGES);

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  const goTo = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(imgs.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  if (imgs.length <= 1) {
    return (
      <Slide src={imgs[0]} alt={alt} onLoad={onFirstLoad} zoomOnHover />
    );
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {imgs.map((src, i) => (
          <Slide
            key={i}
            src={src}
            alt={`${alt} - foto ${i + 1}`}
            onLoad={i === 0 ? onFirstLoad : undefined}
          />
        ))}
      </div>

      {/* Flechas (solo desktop, aparecen al hacer hover en la card) */}
      {index > 0 && (
        <button
          type="button"
          aria-label="Foto anterior"
          onClick={(e) => goTo(e, index - 1)}
          className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white/90 text-primary shadow-lg opacity-0 group-hover:opacity-100 transition hover:bg-white"
        >
          <BsChevronLeft className="text-sm" />
        </button>
      )}
      {index < imgs.length - 1 && (
        <button
          type="button"
          aria-label="Foto siguiente"
          onClick={(e) => goTo(e, index + 1)}
          className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white/90 text-primary shadow-lg opacity-0 group-hover:opacity-100 transition hover:bg-white"
        >
          <BsChevronRight className="text-sm" />
        </button>
      )}

      {/* Puntitos indicadores */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
        {imgs.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Ir a la foto ${i + 1}`}
            onClick={(e) => goTo(e, i)}
            className={`rounded-full transition-all duration-300 ${
              i === index
                ? "w-2 h-2 bg-white shadow"
                : "w-1.5 h-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default RoomImageCarousel;
